/**
 * Text — React (web) implementation.
 *
 * All styling references the shared variant maps in Text.ts,
 * which in turn reference the colour/typography elements.
 * No raw hex values or pixel font sizes in this file.
 */

import React from 'react';
import type { TextProps, TextSegment } from './Text';
import {
  typographyForVariant,
  colorForName,
  ariaAttributesForRole,
  preventOrphan,
  textInsetPadding,
  textSegmentFontStyle,
} from './Text';

export interface WebTextProps extends Partial<Omit<TextProps, 'value'>> {
  /** Plain string content. Optional when `children` is supplied for inline composition. */
  value?: string;
  /** Inline children — nested text runs that flow as a single block. Takes precedence over `value`. */
  children?: React.ReactNode;
  /** Inline rich runs — each `bold` run renders at bold weight; takes precedence over `value`. */
  segments?: TextSegment[];
  'data-testid'?: string;
  'aria-label'?: string;
  /**
   * Whether to bind the final two words with a non-breaking space so a short
   * last word never wraps alone. Defaults to `true`, the long-standing
   * behaviour, so no existing caller changes.
   *
   * Pass `false` in a container narrow enough that the string must wrap. The
   * binding consumes the LAST space, so a two-word label loses its only break
   * opportunity and the engine has to split the word instead — an orphan is
   * traded for a mid-word break, which is worse.
   */
  preventOrphanBinding?: boolean;
}

export function Text({
  variant = 'b1Regular',
  color = 'primary',
  align = 'left',
  role = 'text',
  value,
  segments,
  children,
  numberOfLines,
  'aria-label': ariaLabel,
  preventOrphanBinding = true,
  accessible,
  inset,
  ...rest
}: WebTextProps) {
  const typo = typographyForVariant[variant];
  const fg = colorForName[color];
  // Orphan prevention applies to plain-string content only; inline ReactNode
  // children (e.g. per-word timed reveals) are rendered untouched. Rich `segments`
  // render as nested spans (bold runs at bold weight) and take precedence.
  const content = children ?? value;
  const rendered =
    segments && segments.length > 0
      ? segments.map((segment, index) => (
          <span key={index} style={textSegmentFontStyle(segment)}>
            {segment.text}
          </span>
        ))
      : typeof content === 'string'
        ? preventOrphanBinding
          ? preventOrphan(content)
          : content
        : content;

  const style: React.CSSProperties = {
    fontFamily: typo.fontFamily,
    fontWeight: typo.fontWeight,
    fontStyle: 'fontStyle' in typo ? typo.fontStyle : undefined,
    fontSize: typo.fontSize,
    lineHeight: `${typo.lineHeight}px`,
    letterSpacing: typo.letterSpacing,
    color: fg,
    textAlign: align,
    ...(inset
      ? { paddingLeft: textInsetPadding(inset), paddingRight: textInsetPadding(inset) }
      : null),
    // Web parity for RN's numberOfLines: clamp to N lines with an ellipsis.
    ...(numberOfLines
      ? {
          display: '-webkit-box',
          WebkitLineClamp: numberOfLines,
          WebkitBoxOrient: 'vertical',
          overflow: 'hidden',
        }
      : null),
  };

  return (
    <span
      style={style}
      data-testid={rest['data-testid']}
      aria-label={ariaLabel}
      aria-hidden={accessible === false ? true : undefined}
      {...ariaAttributesForRole(role)}
    >
      {rendered}
    </span>
  );
}
